import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useAuth, type LoginPayload, type CaptchaData, type LoginCrdaPayload, type LoginApufidcPayload } from '../../context/AuthContext';
import AuthField from './AuthField';
import { Skeleton } from '@/components/base/Skeleton';
import ulbService, { type ULBListItem } from '../../modules/utility/shared/services/admin/ulbService';
import './login-form.css';

// LocalStorage key for remembering last selected ULB
const LAST_ULB_KEY = 'last_selected_ulb';

/**
 * Colour schemes for the form's accents.
 *
 * Every class here is a complete literal string rather than an interpolated
 * fragment: Tailwind scans source text, so a class built as `ring-${scheme}-500`
 * is never generated and the style silently goes missing (which is exactly what
 * the captcha field used to do).
 *
 * `gov` is the deep-blue treatment used by the DTCP sign-in page — the primary
 * action on a state government application should read as the government's
 * colour, not as a highlight.
 *
 * `sunset` is the warm light-orange treatment that matches the DTCP sign-in
 * artwork's sky; it is what the sign-in page uses now.
 */
const SCHEMES = {
  sunset: {
    accent: '#c96f1a',
    accentGlow: 'rgba(214,122,36,0.18)',
    button:
      'bg-gradient-to-b from-[#e8913a] to-[#d0731c] hover:from-[#e08428] hover:to-[#bf6512] focus-visible:ring-[#d0731c]',
    link: 'text-[#b5600f] hover:text-[#8f4a0a] dark:text-amber-300 dark:hover:text-amber-200',
    checkbox: 'text-[#d0731c] focus:ring-[#d0731c]',
  },
  gov: {
    accent: '#123262',
    accentGlow: 'rgba(18,50,98,0.14)',
    button:
      'bg-gradient-to-b from-[#1b4585] to-[#0f2c59] hover:from-[#20519b] hover:to-[#143567] focus-visible:ring-[#1b4585]',
    link: 'text-[#123262] hover:text-[#0a1e3f] dark:text-blue-300 dark:hover:text-blue-200',
    checkbox: 'text-[#123262] focus:ring-[#1b4585]',
  },
  amber: {
    accent: '#d97706',
    accentGlow: 'rgba(217,119,6,0.16)',
    button:
      'bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-700 focus-visible:ring-amber-500',
    link: 'text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300',
    checkbox: 'text-amber-600 focus:ring-amber-500',
  },
  blue: {
    accent: '#2563eb',
    accentGlow: 'rgba(37,99,235,0.16)',
    button:
      'bg-gradient-to-b from-blue-600 to-blue-700 hover:from-blue-600 hover:to-blue-800 focus-visible:ring-blue-500',
    link: 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300',
    checkbox: 'text-blue-600 focus:ring-blue-500',
  },
  green: {
    accent: '#16a34a',
    accentGlow: 'rgba(22,163,74,0.16)',
    button:
      'bg-gradient-to-b from-green-600 to-green-700 hover:from-green-600 hover:to-green-800 focus-visible:ring-green-500',
    link: 'text-green-700 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300',
    checkbox: 'text-green-600 focus:ring-green-500',
  },
} as const;

// Schema with ULB required
const loginSchemaERP = yup.object({
  ulb: yup.string().optional(),
  username: yup.string().required('Username is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  captcha: yup.string()
    .required('Captcha is required')
    .matches(/^\d{1,4}$/, 'Please enter a valid answer (1-4 digits)'),
});

// Schema with ULB optional
const loginSchemaCrda = yup.object({
  ulb: yup.string().optional(),
  username: yup.string().required('Username is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  captcha: yup.string()
    .required('Captcha is required')
    .matches(/^\d{1,4}$/, 'Please enter a valid answer (1-4 digits)'),
});

// Schema for APUFIDC - captcha required (uses external captcha API, alphanumeric)
const loginSchemaApufidc = yup.object({
  ulb: yup.string().optional(),
  username: yup.string().required('Username is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  captcha: yup.string()
    .required('Captcha is required')
    .matches(/^[a-zA-Z0-9]{1,6}$/, 'Please enter the captcha text'),
});

type LoginFormData = {
  ulb?: string;
  username: string;
  password: string;
  captcha: string;
};



interface LoginFormProps {
  onSuccess?: () => void;
  autoFocus?: boolean;
  colorScheme?: keyof typeof SCHEMES;
  useERPAuth?: boolean;
  useApufidcAuth?: boolean;
  useCrdaAuth?: boolean;
  siteId?: string; // Portal site ID (prvdr_ste_id) for feature restrictions
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  autoFocus = true,
  colorScheme = 'amber',
  useERPAuth = false,
  useApufidcAuth = false,
  useCrdaAuth = false,
  siteId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [captchaImage, setCaptchaImage] = useState('');
  const [saltKey, setSaltKey] = useState('');
  const [ulbs, setUlbs] = useState<ULBListItem[]>([]);
  const [loadingUlbs, setLoadingUlbs] = useState(false);
  // Drives the one-revolution spin on the refresh control. Presentation only.
  const [captchaSpinning, setCaptchaSpinning] = useState(false);
  const { login, loginCrda, loginApufidc, captcha, externalCaptcha } = useAuth();
  const usernameInputRef = useRef<HTMLInputElement>(null);
  // The backend takes a minute to come back after every restart (and during it the dev proxy
  // answers "Internal server error"). Retry quietly instead of leaving the login stuck on an error.
  const captchaRetryRef = useRef<{ timer?: ReturnType<typeof setTimeout>; tries: number }>({ tries: 0 });
  const ulbsLoadedRef = useRef(false);
  const hideUlBSelect = useApufidcAuth || useCrdaAuth || useERPAuth;

  const scheme = SCHEMES[colorScheme] ?? SCHEMES.amber;

  // Get last selected ULB from localStorage for form initialization
  const savedUlbId = localStorage.getItem(LAST_ULB_KEY) || '';

  // Determine which schema to use based on auth type
  const getSchema = () => {
    if (useApufidcAuth) return loginSchemaApufidc;
    if (useCrdaAuth) return loginSchemaCrda;
    return loginSchemaERP;
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    resetField,
    watch,
  } = useForm<LoginFormData>({
    resolver: yupResolver(getSchema()),
    defaultValues: {
      ulb: savedUlbId,
      username: '',
      password: '',
      captcha: '',
    },
  });

  // Watch ULB field value and calculate selected ULB outside Controller
  const ulbValue = watch('ulb');
  const selectedUlb = useMemo(() => {
    if (!ulbValue) return null;
    if (ulbs.length === 0) return null;
    // Compare as strings since API may return ulb_id as string or number
    return ulbs.find((ulb) => String(ulb.ulb_id) === ulbValue) || null;
  }, [ulbValue, ulbs]);

  useEffect(() => () => {
    if (captchaRetryRef.current.timer) clearTimeout(captchaRetryRef.current.timer);
  }, []);

  useEffect(() => {
    // Only load ULBs if not using APUFIDC/CRDA/ERP auth (These auth types doesn't need ULB selection)
    if (!hideUlBSelect) {
      loadULBs();
    }
    // Load captcha - use external captcha for APUFIDC, standard captcha for others
    getCaptcha();

    if (autoFocus) {
      const timer = setTimeout(() => {
        if (usernameInputRef.current) {
          usernameInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, hideUlBSelect]);

  // Helper function to get user-friendly error message
  const getApiErrorMessage = (error: any, context: string): string => {
    const errorMessage = error?.message || error?.data?.message || '';
    const errorStatus = error?.status || error?.response?.status || error?.data?.status;
    const responseData = error?.response?.data || error?.data || {};

    // Custom status 700 - Database Query/Connection Error (server-specific)
    if (errorStatus === 700 || responseData?.status === 700 ||
      errorMessage.includes('Database Query/Connection Error')) {
      return 'Unable to connect to the database. The server may be undergoing maintenance. Please try again after a few minutes.';
    }

    // Network errors (server not reachable)
    if (error?.code === 'ERR_NETWORK' || errorMessage.includes('Network Error') || errorMessage.includes('Unable to connect')) {
      return 'Unable to connect to the server. Please check your internet connection or try again later.';
    }

    // Timeout errors
    if (error?.code === 'ECONNABORTED' || errorMessage.includes('timeout')) {
      return 'The server is taking too long to respond. Please try again later.';
    }

    // Database connection errors (generic detection)
    if (errorMessage.toLowerCase().includes('database') ||
      errorMessage.toLowerCase().includes('connection refused') ||
      errorMessage.toLowerCase().includes('econnrefused') ||
      errorMessage.toLowerCase().includes('postgres') ||
      errorMessage.toLowerCase().includes('sql')) {
      return 'Unable to connect to the database. The server may be undergoing maintenance. Please try again after a few minutes.';
    }

    // Server errors (500, 502, 503, etc.)
    if (errorStatus >= 500 && errorStatus < 600) {
      return 'The server encountered an error. Please try again later or contact support if the issue persists.';
    }

    // Service unavailable
    if (errorStatus === 503) {
      return 'Service is temporarily unavailable. The server may be undergoing maintenance.';
    }

    // Custom error statuses in 7xx range (application-specific errors)
    if (errorStatus >= 700 && errorStatus < 800) {
      return 'The server encountered an error. Please try again later.';
    }

    // Default message based on context
    if (context === 'captcha') {
      return 'Unable to load captcha. Please refresh the page or try again later.';
    }
    if (context === 'ulb') {
      return 'Unable to load ULB list. Please refresh the page or try again later.';
    }

    return 'Unable to reach the server. Please check your connection and try again.';
  };

  const loadULBs = async () => {
    try {
      setLoadingUlbs(true);
      setApiError('');
      const response = await ulbService.getActiveULBs();
      if (response?.data && Array.isArray(response.data)) {
        // Add "State/Regional Level" option at the beginning
        const stateRegionalOption: ULBListItem = {
          ulb_id: 0,
          ulb_nm: 'State/Regional Level',
          ulb_cd: 'STATE',
          dstrt_id: 0,
        };
        const ulbList = [stateRegionalOption, ...response.data];
        setUlbs(ulbList);
        ulbsLoadedRef.current = true;

        // Restore last selected ULB from localStorage after state is set
        const lastUlbId = localStorage.getItem(LAST_ULB_KEY);
        if (lastUlbId !== null) {
          // Compare as strings since API may return ulb_id as string or number
          const ulbExists = ulbList.some(ulb => String(ulb.ulb_id) === lastUlbId);
          if (ulbExists) {
            // Use setTimeout to ensure state update is complete before setting form value
            setTimeout(() => {
              setValue('ulb', lastUlbId, { shouldValidate: true, shouldDirty: false });
            }, 100);
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading ULBs:', error);
      setApiError(getApiErrorMessage(error, 'ulb'));
    } finally {
      setLoadingUlbs(false);
    }
  };

  const scheduleCaptchaRetry = (): boolean => {
    const retry = captchaRetryRef.current;
    if (retry.tries >= 40) return false; // ~2 minutes, then show the normal error + Retry button
    retry.tries += 1;
    if (retry.timer) clearTimeout(retry.timer);
    retry.timer = setTimeout(() => {
      if (!hideUlBSelect && !ulbsLoadedRef.current) loadULBs();
      getCaptcha();
    }, 3000);
    return true;
  };

  const getCaptcha = async () => {
    try {
      // Use external captcha for APUFIDC, standard captcha for others
      const response: CaptchaData | null = useApufidcAuth ? await externalCaptcha() : await captcha();
      if (response !== null) {
        setCaptchaId(response.cptch_id);
        setSaltKey(response.salt_ky);
        setCaptchaImage(response.cptch_img_tx);
        setApiError(''); // Clear API error on successful captcha load
        captchaRetryRef.current.tries = 0;
      } else if (scheduleCaptchaRetry()) {
        setApiError('The server is starting up. Reconnecting automatically...');
      } else {
        // Response is null - server returned empty response
        setApiError('Unable to load captcha. The server may be experiencing issues.');
      }
    } catch (error: any) {
      console.error('Error loading captcha:', error);
      if (scheduleCaptchaRetry()) {
        setApiError('The server is starting up. Reconnecting automatically...');
      } else {
        setApiError(getApiErrorMessage(error, 'captcha'));
      }
    }
  };

  /** Refresh triggered by the user, so it also runs the spin affordance. */
  const handleRefreshCaptcha = () => {
    setCaptchaSpinning(true);
    getCaptcha();
  };

  /**
   * Encode password with Base64 for obfuscation (defense-in-depth layer)
   * NOTE: This is NOT encryption! Base64 is easily reversible.
   * Real security comes from HTTPS (transport) and bcrypt (storage).
   * This is for compliance/audit purposes to avoid plaintext visibility.
   * @param password - User's password
   * @param saltKey - Random salt from captcha (for added obfuscation)
   * @returns Base64 encoded string
   */
  const encodePassword = (password: string, saltKey: string): string => {
    // Combine password with salt key
    const combined = password + saltKey;

    // Base64 encode (obfuscation, not encryption)
    return btoa(combined);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      if (useApufidcAuth) {
        // APUFIDC uses external API with captcha verification
        await loginApufidc({
          username: data.username,
          password: data.password,
          site_id: siteId || undefined, // Pass portal site ID (prvdr_ste_id)
          captcha_id: captchaId || 0,
          captcha: data.captcha,
        } as LoginApufidcPayload);
      } else if (useCrdaAuth) {
        // CRDA authentication (without ULB selection)
        await loginCrda({
          username: data.username,
          password: data.password,
          captcha_id: captchaId || 0,
          captcha: data.captcha,
        } as LoginCrdaPayload);
      } else {
        // Standard ERP authentication with Base64 password obfuscation
        // Encode password with saltKey for defense-in-depth (obfuscation layer)
        const encodedPassword = saltKey ? encodePassword(data.password, saltKey) : data.password;

        await login({
          username: data.username,
          password: encodedPassword,  // Base64 encoded (e.g., "TXlQYXNzd29yZDEyM2FiYzEyMw==")
          captcha: data.captcha,
          captcha_id: captchaId || 0
        } as LoginPayload);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
      // Refresh captcha on login failure for all auth types
      getCaptcha();
      resetField('captcha');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine error icon and title based on error message
  const getErrorDisplay = (errorMsg: string) => {
    // Database errors (including status 700)
    if (errorMsg.includes('database') || errorMsg.includes('Database')) {
      return { icon: 'ri-database-2-line', title: 'Database Connection Error' };
    }
    // Network/internet connection errors
    if (errorMsg.includes('internet') || errorMsg.includes('Network') || errorMsg.includes('connect to the server')) {
      return { icon: 'ri-wifi-off-line', title: 'Network Connection Error' };
    }
    // Timeout errors
    if (errorMsg.includes('timeout') || errorMsg.includes('taking too long')) {
      return { icon: 'ri-time-line', title: 'Server Timeout' };
    }
    // Maintenance errors
    if (errorMsg.includes('maintenance')) {
      return { icon: 'ri-tools-line', title: 'Server Maintenance' };
    }
    // Captcha-specific errors
    if (errorMsg.includes('captcha') || errorMsg.includes('Captcha')) {
      return { icon: 'ri-shield-check-line', title: 'Captcha Error' };
    }
    // ULB list errors
    if (errorMsg.includes('ULB')) {
      return { icon: 'ri-list-check', title: 'Data Loading Error' };
    }
    // Default server error
    return { icon: 'ri-server-line', title: 'Server Connection Error' };
  };

  const errorDisplay = apiError ? getErrorDisplay(apiError) : null;
  const captchaLength = useApufidcAuth ? 6 : 4;

  return (<>
    {/* API Server Error Banner */}
    {apiError && errorDisplay && (
      <div
        role="status"
        className="mb-5 rounded-xl border border-amber-300/70 bg-amber-50 p-4 dark:border-amber-700/60 dark:bg-amber-900/20"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
            <i className={`${errorDisplay.icon} text-[17px] text-amber-600 dark:text-amber-400`} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">{errorDisplay.title}</h4>
            <p className="mt-1 text-[13px] leading-relaxed text-amber-800/90 dark:text-amber-300/90">{apiError}</p>
            <button
              type="button"
              onClick={() => { setApiError(''); loadULBs(); getCaptcha(); }}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-md text-[13px] font-semibold text-amber-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-300"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              Retry connection
            </button>
          </div>
        </div>
      </div>
    )}

    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5" noValidate>
      {error && (
        <div
          role="alert"
          className="dtcp-nudge rounded-xl border border-red-200 bg-red-50 p-3.5 dark:border-red-800/60 dark:bg-red-900/20"
        >
          <div className="flex items-start gap-2.5">
            <i className="ri-error-warning-fill mt-px text-[16px] text-red-500" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-red-800 dark:text-red-300">Sign-in failed</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-red-700/90 dark:text-red-400/90">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* ULB Selection */}
      {!hideUlBSelect && (
        <div>
          <label className="block text-[13px] font-semibold text-slate-700 dark:text-gray-300">
            Select ULB
          </label>
          <p className="mb-2 mt-0.5 text-xs text-slate-500 dark:text-gray-400">
            State/Regional users: select "State/Regional Level"
          </p>
          <Controller
            name="ulb"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={ulbs}
                getOptionLabel={(option) => option.ulb_nm || ''}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.ulb_id === value.ulb_id;
                }}
                value={selectedUlb}
                onChange={(_, newValue) => {
                  if (newValue) {
                    const ulbId = newValue.ulb_id.toString();
                    field.onChange(ulbId);
                    // Save to localStorage for next login
                    localStorage.setItem(LAST_ULB_KEY, ulbId);
                  } else {
                    field.onChange('');
                    localStorage.removeItem(LAST_ULB_KEY);
                  }
                }}
                onBlur={field.onBlur}
                clearOnBlur={false}
                clearOnEscape
                disableClearable={false}
                readOnly={false}
                selectOnFocus
                handleHomeEndKeys
                disabled={loadingUlbs}
                loading={loadingUlbs}
                autoComplete={false}
                autoHighlight={true}
                autoSelect={false}
                freeSolo={false}
                clearIcon={<i className="ri-close-circle-fill text-gray-400 hover:text-gray-600 text-lg cursor-pointer"></i>}
                sx={{
                  '& .MuiAutocomplete-clearIndicator': {
                    visibility: selectedUlb ? 'visible' : 'hidden',
                  },
                }}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <React.Fragment key={option.ulb_id}>
                      <li key={key} {...restProps}>
                        <div className="flex items-center w-full py-1">
                          {option.ulb_id === 0 ? (
                            <>
                              <i className="ri-government-line mr-2 text-lg" style={{ color: scheme.accent }}></i>
                              <div>
                                <div className="font-semibold" style={{ color: scheme.accent }}>{option.ulb_nm}</div>
                                <div className="text-xs text-gray-500">Multi-ULB access</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <i className="ri-building-line text-gray-400 mr-2"></i>
                              <span>{option.ulb_nm}</span>
                            </>
                          )}
                        </div>
                      </li>
                      {/* Add divider after "State/Regional Level" option */}
                      {option.ulb_id === 0 && (
                        <li className="border-b border-gray-200 dark:border-gray-600 mx-2 my-1"></li>
                      )}
                    </React.Fragment>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={loadingUlbs ? 'Loading...' : 'Search ULB...'}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <i
                            className={`${selectedUlb?.ulb_id === 0 ? 'ri-government-line' : 'ri-building-line text-gray-400'} ml-2 mr-1`}
                            style={selectedUlb?.ulb_id === 0 ? { color: scheme.accent } : undefined}
                          ></i>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingUlbs ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                        '& fieldset': {
                          borderColor: 'rgb(203 213 225)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgb(148 163 184)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: scheme.accent,
                          borderWidth: '1px',
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 3px ${scheme.accentGlow}`,
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '10.5px 14px',
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        visibility: 'visible',
                        color: 'rgb(156 163 175)',
                        '&:hover': {
                          color: 'rgb(75 85 99)',
                        },
                      },
                    }}
                  />
                )}
              />
            )}
          />
          {errors.ulb && (
            <p role="alert" className="mt-1.5 text-[12.5px] font-medium text-red-600 dark:text-red-400">
              {errors.ulb.message}
            </p>
          )}
        </div>
      )}

      <AuthField
        label="Username"
        id="login-username"
        type="text"
        iconClass="ri-user-3-line"
        placeholder="Enter your username"
        autoComplete="username"
        accent={scheme.accent}
        accentGlow={scheme.accentGlow}
        error={errors.username?.message}
        {...register('username', {
          setValueAs: (value) => value,
        })}
        ref={(e) => {
          register('username').ref(e);
          usernameInputRef.current = e;
        }}
      />

      <AuthField
        label="Password"
        id="login-password"
        type="password"
        iconClass="ri-lock-2-line"
        placeholder="Enter your password"
        autoComplete="current-password"
        accent={scheme.accent}
        accentGlow={scheme.accentGlow}
        error={errors.password?.message}
        {...register('password')}
      />

      {/* ── Captcha ──────────────────────────────────────────────────────
          Grouped on its own surface so it reads as one verification step
          rather than two unrelated controls sitting next to each other. */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-2 flex items-center justify-between gap-2">
          <label
            htmlFor="login-captcha"
            className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 dark:text-gray-300"
          >
            <i className="ri-shield-keyhole-line text-[15px] text-slate-400" aria-hidden="true" />
            Captcha verification
          </label>
          <button
            type="button"
            onClick={handleRefreshCaptcha}
            className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${scheme.link}`}
            aria-label="Load a new captcha image"
          >
            <i
              className={`ri-refresh-line text-[14px] ${captchaSpinning ? 'dtcp-spin' : ''}`}
              onAnimationEnd={() => setCaptchaSpinning(false)}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>

        <div className="flex items-start gap-2.5">
          {/* Captcha image. The plate is `bg-[#ffffff]`, not `bg-white`:
              index.css repaints every `.dark .bg-white` to the dark card colour
              with `!important`, which would leave the challenge as black line
              work on a near-black plate — an unreadable captcha in dark mode. */}
          <div className="w-[136px] shrink-0">
            {captchaImage ? (
              <img
                key={captchaImage}
                src={captchaImage}
                alt="Captcha challenge. Use the refresh control for a new one."
                className="dtcp-swap-in h-11 w-full rounded-lg border border-slate-300 bg-[#ffffff] object-contain dark:border-gray-600"
                onError={() => {
                  console.error('Captcha image failed to load');
                }}
              />
            ) : (
              <Skeleton shape="rounded" className="h-11 w-full" />
            )}
          </div>

          {/* Answer */}
          <div className="min-w-0 flex-1">
            <input
              id="login-captcha"
              type="text"
              placeholder={useApufidcAuth ? 'Enter text' : 'Enter answer'}
              className={`dtcp-field h-11 w-full rounded-lg px-3.5 text-center text-[15px] font-semibold text-slate-900 placeholder:font-normal placeholder:tracking-normal placeholder-slate-400 dark:text-white dark:placeholder-gray-500 ${errors.captcha ? 'dtcp-field--error' : ''}`}
              style={
                {
                  letterSpacing: '0.35em',
                  '--dtcp-field-accent': scheme.accent,
                  '--dtcp-field-glow': scheme.accentGlow,
                } as React.CSSProperties
              }
              maxLength={captchaLength}
              pattern={useApufidcAuth ? '[a-zA-Z0-9]*' : '[0-9]*'}
              inputMode={useApufidcAuth ? 'text' : 'numeric'}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-invalid={!!errors.captcha}
              aria-describedby={errors.captcha ? 'login-captcha-error' : undefined}
              {...register('captcha')}
              onChange={(e) => {
                const value = useApufidcAuth
                  ? e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6)
                  : e.target.value.replace(/\D/g, '').slice(0, 4);
                e.target.value = value;
                setValue('captcha', value);
              }}
            />
          </div>
        </div>

        {errors.captcha && (
          <p
            id="login-captcha-error"
            role="alert"
            className="dtcp-nudge mt-2 flex items-start gap-1.5 text-[12.5px] font-medium text-red-600 dark:text-red-400"
          >
            <i className="ri-error-warning-line mt-px text-[13px]" aria-hidden="true" />
            <span>{errors.captcha.message}</span>
          </p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center justify-between pt-0.5">
        <div className="flex items-center">
          <input
            id="remember-me"
            type="checkbox"
            className={`h-4 w-4 rounded border-slate-300 focus:ring-2 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 ${scheme.checkbox}`}
          />
          <label
            htmlFor="remember-me"
            className="ml-2 select-none text-[13px] text-slate-600 dark:text-gray-300"
          >
            Remember me
          </label>
        </div>

        <a
          href="/forgot-password"
          className={`text-[13px] font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded ${scheme.link}`}
        >
          Forgot password?
        </a>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        aria-busy={isLoading}
        className={`flex h-11 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-semibold tracking-wide text-white shadow-sm transition-all duration-200 hover:-translate-y-px hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-sm ${scheme.button}`}
      >
        {isLoading ? (
          <>
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden="true"
            />
            Signing in&hellip;
          </>
        ) : (
          <>
            <i className="ri-shield-user-line text-[17px]" aria-hidden="true" />
            Sign in
          </>
        )}
      </button>
    </form>
  </>
  );
};
